import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import MobilePatternHeader from "../components/MobilePatternHeader";

/**
 * MobilePolicy — Animated, accessible, mobile-first policy screen
 * ---------------------------------------------------------------
 * Highlights:
 * 1) Sticky, scrollable tabs with animated indicator (shared layoutId)
 * 2) Smooth page + tab transitions that respect "prefers-reduced-motion"
 * 3) Keyboard-accessible tabs (Left/Right to switch; Home/End jump)
 * 4) URL-synced state (?tab=usage|privacy|refund|update) for deep link/restore
 * 5) Larger touch targets, safe-area padding, and focus-visible rings
 */

const TABS = [
  { key: "usage", label: "Quy định sử dụng" },
  { key: "privacy", label: "Chính sách bảo mật" },
  { key: "refund", label: "Chính sách hoàn tiền" },
  { key: "update", label: "Chính sách cập nhật" },
];

const sections = {
  usage: {
    title: "Quy định sử dụng hệ thống",
    body: (
      <>
        <p>
          Người dùng cam kết sử dụng hệ thống đúng mục đích, không vi phạm pháp
          luật hoặc gây tổn hại đến người khác...
        </p>
        <ul className="list-disc list-inside ml-5 space-y-1">
          <li>Không đăng tải nội dung độc hại hoặc thông tin giả mạo.</li>
          <li>Không phá hoại hoặc xâm nhập hệ thống, gây gián đoạn dịch vụ.</li>
          <li>Không mạo danh người khác hoặc tổ chức.</li>
        </ul>
        <p>
          Chúng tôi có quyền tạm dừng hoặc chấm dứt tài khoản vi phạm mà không
          cần thông báo trước.
        </p>
      </>
    ),
  },
  privacy: {
    title: "Chính sách bảo mật",
    body: (
      <>
        <p>
          Hệ thống cam kết bảo vệ dữ liệu cá nhân, chỉ thu thập thông tin cần
          thiết để cung cấp dịch vụ hiệu quả.
        </p>
        <p>
          Các loại dữ liệu có thể thu thập gồm: họ tên, email, lịch sử sử dụng
          dịch vụ...
        </p>
        <p>
          Người dùng có thể yêu cầu truy cập, sửa đổi hoặc xóa dữ liệu cá nhân
          bất cứ lúc nào.
        </p>
      </>
    ),
  },
  refund: {
    title: "Chính sách hoàn tiền",
    body: (
      <>
        <p>
          Chúng tôi hỗ trợ hoàn tiền trong vòng 7 ngày nếu người dùng chưa sử
          dụng dịch vụ hoặc hệ thống gặp sự cố kỹ thuật nghiêm trọng.
        </p>
        <p>
          Các trường hợp không hoàn tiền bao gồm: vi phạm điều khoản, đã sử dụng
          dịch vụ đáng kể, yêu cầu sau thời gian cho phép...
        </p>
      </>
    ),
  },
  update: {
    title: "Chính sách cập nhật & thông báo",
    body: (
      <>
        <p>
          Hệ thống có thể cập nhật nội dung, điều khoản hoặc chức năng mà không
          cần báo trước.
        </p>
        <p>
          Chúng tôi sẽ thông báo các cập nhật thông qua email hoặc thông báo
          trong hệ thống.
        </p>
        <p>
          Việc tiếp tục sử dụng sau cập nhật đồng nghĩa bạn đã đồng ý với thay
          đổi đó.
        </p>
      </>
    ),
  },
};

const tabKeys = TABS.map((t) => t.key);

export default function MobilePolicy() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const reduced = useReducedMotion();
  const headerRef = useRef(null);

  // 1) URL-sync: read default from ?tab=... (fallback "usage")
  const initialTab = useMemo(() => {
    const q = params.get("tab");
    return tabKeys.includes(q || "") ? q : "usage";
  }, []);

  const [activeTab, setActiveTab] = useState(initialTab);

  // Keep URL in sync when activeTab changes
  useEffect(() => {
    const current = params.get("tab");
    if (current !== activeTab) {
      const next = new URLSearchParams(params);
      next.set("tab", activeTab);
      setParams(next, { replace: true });
    }
  }, [activeTab]);

  // 2) Keyboard navigation for tabs
  const onTabKeyDown = (e) => {
    const idx = tabKeys.indexOf(activeTab);
    if (e.key === "ArrowRight") {
      e.preventDefault();
      setActiveTab(tabKeys[(idx + 1) % tabKeys.length]);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      setActiveTab(tabKeys[(idx - 1 + tabKeys.length) % tabKeys.length]);
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveTab(tabKeys[0]);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveTab(tabKeys[tabKeys.length - 1]);
    }
  };

  // Motion helpers
  const pageVariants = {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0, transition: { duration: reduced ? 0 : 0.18 } },
    exit: { opacity: 0, y: 6, transition: { duration: reduced ? 0 : 0.12 } },
  };

  const contentVariants = {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0, transition: { duration: reduced ? 0 : 0.18 } },
    exit: { opacity: 0, y: 6, transition: { duration: reduced ? 0 : 0.12 } },
  };

  return (
    <motion.div
      className="flex h-screen w-screen flex-col bg-[#f2f2f2]"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
      // Safe areas for iOS notches
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* Top bar */}
      <div ref={headerRef}>
        <MobilePatternHeader title="Chính sách" onBack={() => navigate(-1)} />
      </div>

      {/* Tabs */}
      <div
        role="tablist"
        aria-label="Chuyển mục chính sách"
        onKeyDown={onTabKeyDown}
        className="sticky top-0 z-10 flex w-full gap-2 overflow-x-auto border-b bg-white pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {TABS.map((tab) => {
          const selected = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              role="tab"
              aria-selected={selected}
              aria-controls={`panel-${tab.key}`}
              id={`tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className={`relative mr-1 inline-flex min-w-[max(40%,8rem)] shrink-0 items-center justify-center rounded-md px-4 py-3 text-sm font-medium outline-none transition-colors focus-visible:ring focus-visible:ring-blue-500 md:min-w-0 md:px-3 md:py-2 ${
                selected ? "text-blue-700" : "text-gray-600 hover:text-blue-600"
              }`}
            >
              {tab.label}
              {selected && (
                <motion.span
                  layoutId="tab-underline"
                  className="absolute -bottom-[2px] left-2 right-2 h-[2px] rounded bg-blue-600"
                  transition={{
                    type: "spring",
                    stiffness: 500,
                    damping: 40,
                    duration: reduced ? 0 : 0.22,
                  }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Content area */}
      <div className="relative flex-1 overflow-y-auto px-4 py-4">
        <AnimatePresence mode="wait">
          <motion.section
            key={activeTab}
            id={`panel-${activeTab}`}
            role="tabpanel"
            aria-labelledby={`tab-${activeTab}`}
            variants={contentVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="mx-auto max-w-prose text-[15px] leading-relaxed text-gray-700"
          >
            <h2 className="mb-3 text-xl font-semibold text-gray-900">
              {sections[activeTab].title}
            </h2>
            <div className="space-y-4">{sections[activeTab].body}</div>
          </motion.section>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
